import fs from "fs";
import path from "path";
import matter from "gray-matter";
import { FEATURED_FACILITIES } from "./featuredFacilities.js";

function getBlogPath() {
  return path.join(process.cwd(), "content", "blog");
}

function isDev() {
  return process.env.NODE_ENV === "development";
}

export const REGION_LABELS = {
  ca: "Canada",
  au: "Australia",
  nz: "New Zealand",
};

export function isPublished(post) {
  return isDev() || (!post.draft && new Date(post.publishedAt) <= new Date());
}

export async function getAllBlogPosts() {
  const markdownFiles = listMarkdownFiles();

  const posts = await Promise.all(
    markdownFiles.map(async (filename) => {
      const filePath = path.join(getBlogPath(), filename);
      const { data: frontmatter, content } =
        readFrontmatterAndContent(filePath);
      const slug = filenameToSlug(filename);
      return composePost({
        slug,
        frontmatter,
        content,
        includeContent: true,
      });
    }),
  );

  return filterAndSortPosts(posts);
}

export async function getBlogPost(slug) {
  if (!/^[a-zA-Z0-9_-]+$/.test(slug)) return null;

  try {
    const filePath = path.join(getBlogPath(), `${slug}.md`);
    const { data: frontmatter, content } = readFrontmatterAndContent(filePath);
    const post = composePost({
      slug,
      frontmatter,
      content,
      includeContent: true,
    });
    if (!isPublished(post)) return null;
    return post;
  } catch {
    return null;
  }
}

export async function getBlogPostMetadata({ region } = {}) {
  const markdownFiles = listMarkdownFiles();

  const posts = filterAndSortPosts(
    markdownFiles.map((filename) => {
      const filePath = path.join(getBlogPath(), filename);
      const { data: frontmatter, content } =
        readFrontmatterAndContent(filePath);
      const slug = filenameToSlug(filename);
      return composePost({
        slug,
        frontmatter,
        content,
        includeContent: false,
      });
    }),
  );

  if (region) return posts.filter((p) => p.region === region);
  return posts;
}

export async function getRelatedPosts(currentSlug, region, limit = 3) {
  if (!region) return [];
  const posts = await getBlogPostMetadata({ region });
  const related = posts.filter((p) => p.slug !== currentSlug);
  return related.slice(0, limit);
}

export function getCTAConfig(post) {
  // Override fields take precedence
  if (post.ctaHeading) {
    return {
      heading: post.ctaHeading,
      body: post.ctaBody || "",
      buttonText: post.ctaButton || "Get started",
      buttonLink: post.ctaLink || "/new",
    };
  }

  // Derive from ctaFacilitySlug or facilitySlug
  const effectiveSlug = post.ctaFacilitySlug || post.facilitySlug;
  if (effectiveSlug) {
    const facility = FEATURED_FACILITIES.find((f) => f.slug === effectiveSlug);
    if (facility) {
      return {
        heading: `${facility.name} booked out?`,
        body: "Schnerp monitors cancellations 24/7 and alerts you the moment a spot opens up.",
        buttonText: `Watch ${facility.name}`,
        buttonLink: `/new/${facility.slug}`,
      };
    }
    if (isDev()) {
      console.warn(
        `[blog] facilitySlug "${effectiveSlug}" not found in FEATURED_FACILITIES — using generic CTA`,
      );
    }
  }

  // Derive from ctaVariant
  const variants = {
    comparison: {
      heading: "See for yourself",
      body: "Start a free request and see how Schnerp works, no credit card required.",
      buttonText: "Try Schnerp free",
      buttonLink: "/new",
    },
    data: {
      heading: "Get the cancellation alert",
      body: "See the data in action, Schnerp watches for cancellations so you don't have to.",
      buttonText: "Watch a campground",
      buttonLink: "/new",
    },
  };

  if (post.ctaVariant && variants[post.ctaVariant]) {
    return variants[post.ctaVariant];
  }

  // Generic fallback (pillar, seasonal, or unspecified)
  return {
    heading: "Stop refreshing. Start camping.",
    body: "Schnerp watches for cancellations across Canada, Australia, and New Zealand, and alerts you the moment a spot opens up.",
    buttonText: "Find your spot",
    buttonLink: "/new",
  };
}

function composePost({ slug, frontmatter, content, includeContent }) {
  const base = {
    slug,
    title: frontmatter.title || "Untitled",
    excerpt: frontmatter.excerpt || "",
    publishedAt: (frontmatter.publishedAt || new Date().toISOString()).slice(
      0,
      10,
    ),
    // Optional. When set (and a valid date), the post shows an "Updated" date
    // and emits dateModified / article:modified_time for SEO freshness signals.
    updatedAt: normalizeDate(frontmatter.updatedAt),
    readTime: frontmatter.readTime || calculateReadTime(content),
    author: frontmatter.author || { name: "Schnerp Team" },
    featuredImage: frontmatter.featuredImage || null,
    // Attribution rendered as a caption under the featured image (required for
    // Unsplash/UGC photos). `Credit` is the photographer name; `CreditUrl` is
    // their profile link (Unsplash profiles carry utm referral params). When
    // the URL is an unsplash.com link the caption adds the "on Unsplash" link
    // per Unsplash's attribution guideline.
    featuredImageCredit: frontmatter.featuredImageCredit || null,
    featuredImageCreditUrl: frontmatter.featuredImageCreditUrl || null,
    tags: frontmatter.tags || [],
    draft: frontmatter.draft || false,
    region: frontmatter.region || null,
    ctaFacilitySlug: frontmatter.ctaFacilitySlug || null,
    ctaVariant: frontmatter.ctaVariant || null,
    ctaHeading: frontmatter.ctaHeading || null,
    ctaBody: frontmatter.ctaBody || null,
    ctaButton: frontmatter.ctaButton || null,
    ctaLink: frontmatter.ctaLink || null,
    emailCapture: frontmatter.emailCapture || null,
    facilitySlug: frontmatter.facilitySlug || null,
    // Optional per-post canonical override. When set, the blog post's
    // canonical <link> points here instead of /blog/<slug>, telling search
    // engines to consolidate SEO weight onto a different page. Used by the
    // Alberta Parks update post to point at the evergreen /alberta-parks
    // explainer. Path is site-relative (e.g. "/alberta-parks").
    canonicalPath: frontmatter.canonicalPath || null,
  };
  return includeContent ? { ...base, content } : base;
}

function listMarkdownFiles() {
  const files = fs.readdirSync(getBlogPath());
  return files.filter((file) => file.endsWith(".md"));
}

function readFrontmatterAndContent(filePath) {
  const fileContents = fs.readFileSync(filePath, "utf8");
  return matter(fileContents);
}

function filenameToSlug(filename) {
  return filename.replace(/\.md$/, "");
}

function filterAndSortPosts(posts) {
  const published = posts.filter((post) => isPublished(post));
  return published.sort(
    (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt),
  );
}

function normalizeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}

function calculateReadTime(content) {
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  const readTime = Math.ceil(wordCount / wordsPerMinute);
  return readTime;
}
